import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../../hooks/useTranslation';
import {
  landingFrameInset,
  landingFrameInsetTablet,
  landingFrameInsetMobile,
} from './LandingViewportChrome';

const wordmarkShimmerDark = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

export const LANDING_NAV_LINKS = [
  { id: 'features', href: '/#features' },
  { id: 'pricing', href: '/#pricing' },
  { id: 'contact', href: '/#contact' },
];

const TopNavShell = styled.div`
  position: fixed;
  top: ${landingFrameInset};
  left: ${landingFrameInset};
  right: ${landingFrameInset};
  z-index: 30;
  padding: ${({ $scrolled }) => ($scrolled ? '0.75rem 0 0' : '0')};
  pointer-events: none;
  transition: padding 0.4s ease;

  @media (max-width: 1024px) {
    top: ${landingFrameInsetTablet};
    left: ${landingFrameInsetTablet};
    right: ${landingFrameInsetTablet};
    padding: ${({ $scrolled }) => ($scrolled ? '0.625rem 0 0' : '0')};
  }

  @media (max-width: 640px) {
    top: ${landingFrameInsetMobile};
    left: ${landingFrameInsetMobile};
    right: ${landingFrameInsetMobile};
  }

  @media (max-width: 480px) {
    padding: ${({ $scrolled }) => ($scrolled ? '0.5rem 0 0' : '0')};
  }
`;

const TopNavBar = styled.nav`
  position: relative;
  z-index: 30;
  width: 100%;
  padding: 0 1rem;
  color: #1a1c1c;
  background: transparent;
  pointer-events: auto;
  transition: all 0.5s ease;

  @media (min-width: 1280px) {
    padding: 0 1rem;
  }
`;

const TopNavInner = styled.div`
  max-width: 90rem;
  margin: 0 auto;
  width: 100%;
  padding: ${({ $scrolled }) => ($scrolled ? '0.75rem 1.25rem' : '1rem')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  background: ${({ $scrolled }) =>
    $scrolled ? 'rgba(252, 254, 254, 0.9)' : 'transparent'};
  backdrop-filter: ${({ $scrolled }) =>
    $scrolled ? 'blur(0.875rem) saturate(1.35)' : 'none'};
  -webkit-backdrop-filter: ${({ $scrolled }) =>
    $scrolled ? 'blur(0.875rem) saturate(1.35)' : 'none'};
  border: 1px solid
    ${({ $scrolled }) => ($scrolled ? 'rgba(0, 0, 0, 0.06)' : 'transparent')};
  border-radius: ${({ $scrolled }) => ($scrolled ? '1.25rem' : '0')};
  box-shadow: ${({ $scrolled }) =>
    $scrolled
      ? '0 0.375rem 1.25rem rgba(0, 0, 0, 0.08), 0 0.125rem 0.375rem rgba(0, 0, 0, 0.04)'
      : 'none'};
  transition:
    background 0.4s ease,
    border-color 0.4s ease,
    border-radius 0.4s ease,
    box-shadow 0.4s ease,
    backdrop-filter 0.4s ease,
    -webkit-backdrop-filter 0.4s ease,
    padding 0.4s ease;

  @media (min-width: 1024px) {
    padding: ${({ $scrolled }) => ($scrolled ? '0.625rem 1.25rem' : '0.75rem 1rem')};
  }

  @media (max-width: 1024px) {
    border-radius: ${({ $scrolled }) => ($scrolled ? '1rem' : '0')};
    padding: ${({ $scrolled }) => ($scrolled ? '0.75rem 1rem' : '1rem')};
  }
`;

const TopNavBrand = styled(Link)`
  color: #1a1c1c;
  text-decoration: none;
  font-size: 1.5rem;
  line-height: 2rem;
  font-weight: 700;
  letter-spacing: -0.075rem;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;

  @media (max-width: 1024px) {
    gap: 0.5rem;
  }
`;

const LandingBrandWordmark = styled.span`
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: 1.375rem;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.2;
  flex-shrink: 0;
  background: linear-gradient(
    115deg,
    #003d38 0%,
    #006a64 28%,
    #0d9488 52%,
    #047857 72%,
    #006a64 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 1px 0 rgba(255, 255, 255, 0.85));

  @media (prefers-reduced-motion: no-preference) {
    animation: ${wordmarkShimmerDark} 11s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background-position: 0% 50%;
  }

  @media (max-width: 1024px) {
    font-size: 1.125rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const LandingLogoImage = styled.img`
  height: 60px;
  width: auto;
  max-width: 400px;
  margin: 0;
  padding: 0;
  object-fit: contain;
  display: block;
  vertical-align: top;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    height: 46px;
    max-width: 300px;
  }
`;

const TopNavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const TopNavLink = styled.a`
  text-decoration: none;
  color: #3f4948;
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
  transition: color 0.2s ease, background 0.2s ease;

  &:hover {
    color: #006a64;
    background: rgba(0, 106, 100, 0.08);
  }
`;

const TopNavActiveLink = styled(TopNavLink)`
  color: #006a64;
  background: rgba(0, 106, 100, 0.1);
`;

const TopNavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const TopNavLanguageSelect = styled.select`
  border: 1px solid rgba(0, 106, 100, 0.2);
  background: rgba(0, 106, 100, 0.08);
  color: #00514b;
  padding: 0.5rem 2rem 0.5rem 0.875rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, #006a64 50%),
    linear-gradient(135deg, #006a64 50%, transparent 50%);
  background-position:
    calc(100% - 1rem) calc(50% - 0.125rem),
    calc(100% - 0.75rem) calc(50% - 0.125rem);
  background-size: 0.375rem 0.375rem, 0.375rem 0.375rem;
  background-repeat: no-repeat;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    border-color: rgba(0, 106, 100, 0.34);
    background-color: rgba(0, 106, 100, 0.12);
  }

  &:focus-visible {
    outline: none;
    border-color: #006a64;
    box-shadow: 0 0 0 0.1875rem rgba(0, 106, 100, 0.14);
  }

  option {
    background: #f4fbfa;
    color: #1f3b39;
    font-weight: 600;
  }
`;

const TopNavLogin = styled(Link)`
  text-decoration: none;
  color: #3f4948;
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: 500;
  padding: 0.5rem 1.25rem;
  border-radius: 0.375rem;
  transition: color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: #006a64;
    transform: scale(1.03);
  }
`;

const TopNavBookDemo = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  padding: 0.5rem 1.25rem;
  background: linear-gradient(135deg, #52e4d4 0%, #30b8a9 100%);
  color: #ffffff;
  text-decoration: none;
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: 500;
  border: 2px solid transparent;
  box-shadow: 0 4px 6px -1px rgba(0, 106, 100, 0.18);
  transition: transform 0.2s ease, filter 0.2s ease;

  &:hover {
    color: #ffffff;
    filter: brightness(1.03);
    transform: scale(1.03);
  }
`;

const MobileNavActions = styled.div`
  display: none;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 1024px) {
    display: flex;
  }
`;

const MobileLanguageSelect = styled(TopNavLanguageSelect)`
  padding: 0.5rem 1.75rem 0.5rem 0.75rem;
  font-size: 0.8125rem;
  line-height: 1.125rem;
`;

const MobileMenuButton = styled.button`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.625rem;
  border: 1px solid rgba(0, 106, 100, 0.12);
  background: rgba(255, 255, 255, 0.95);
  color: #1a1c1c;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    border-color: rgba(0, 106, 100, 0.28);
    box-shadow: 0 0.25rem 0.75rem rgba(0, 106, 100, 0.1);
  }

  &:focus-visible {
    outline: none;
    border-color: #006a64;
    box-shadow: 0 0 0 0.1875rem rgba(0, 106, 100, 0.14);
  }
`;

const MobileMenuIcon = styled.span`
  position: relative;
  width: 1.125rem;
  height: 0.875rem;

  &::before,
  &::after,
  span {
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    height: 0.125rem;
    background: currentColor;
    border-radius: 9999px;
    transition:
      transform 0.25s ease,
      top 0.25s ease,
      opacity 0.2s ease;
  }

  &::before {
    top: ${({ $open }) => ($open ? '0.375rem' : '0')};
    transform: ${({ $open }) => ($open ? 'rotate(45deg)' : 'none')};
  }

  span {
    top: 0.375rem;
    opacity: ${({ $open }) => ($open ? 0 : 1)};
  }

  &::after {
    top: ${({ $open }) => ($open ? '0.375rem' : '0.75rem')};
    transform: ${({ $open }) => ($open ? 'rotate(-45deg)' : 'none')};
  }
`;

const MobileMenuBackdrop = styled(motion.button)`
  position: fixed;
  inset: 0;
  z-index: 90;
  border: none;
  padding: 0;
  cursor: pointer;
  background: rgba(10, 18, 17, 0.42);
  backdrop-filter: blur(0.375rem);
  -webkit-backdrop-filter: blur(0.375rem);
  display: none;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const MobileMenuPanel = styled(motion.aside)`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 100;
  width: min(18.75rem, 86vw);
  max-width: 100%;
  overflow: hidden;
  display: none;
  flex-direction: column;
  background: linear-gradient(180deg, #fcfefd 0%, #f4fbf9 52%, #eef8f6 100%);
  border-right: 1px solid rgba(0, 106, 100, 0.1);
  box-shadow:
    0.5rem 0 2rem rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.65) inset;

  @media (max-width: 1024px) {
    display: flex;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 9rem;
    height: 9rem;
    background: radial-gradient(circle, rgba(82, 228, 212, 0.22) 0%, transparent 72%);
    pointer-events: none;
  }
`;

const MobileMenuHeader = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1.125rem 1rem 0.875rem;
  border-bottom: 1px solid rgba(0, 106, 100, 0.08);
`;

const MobileMenuBrand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  text-decoration: none;
  color: inherit;
`;

const MobileMenuBrandLogo = styled.img`
  height: 2.25rem;
  width: auto;
  object-fit: contain;
  flex-shrink: 0;
`;

const MobileMenuBrandName = styled.span`
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: 0.9375rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #006a64;
  white-space: nowrap;
`;

const MobileMenuCloseButton = styled.button`
  width: 2.375rem;
  height: 2.375rem;
  border-radius: 0.625rem;
  border: 1px solid rgba(0, 106, 100, 0.14);
  background: rgba(255, 255, 255, 0.92);
  color: #1a1c1c;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    transform 0.2s ease;

  &:hover {
    border-color: rgba(0, 106, 100, 0.3);
    background: #ffffff;
    transform: scale(1.03);
  }

  &:focus-visible {
    outline: none;
    border-color: #006a64;
    box-shadow: 0 0 0 0.1875rem rgba(0, 106, 100, 0.14);
  }
`;

const MobileMenuBody = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
`;

const MobileMenuSectionLabel = styled.p`
  margin: 0 0 0.625rem;
  padding: 0 0.375rem;
  font-size: 0.6875rem;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(0, 106, 100, 0.55);
`;

const MobileMenuLinks = styled(motion.nav)`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const mobileMenuLinkBase = `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  text-decoration: none;
  font-size: 1rem;
  line-height: 1.4;
  font-weight: 600;
  padding: 0.875rem 1rem;
  border-radius: 0.875rem;
  border: 1px solid transparent;
  transition:
    color 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
`;

const MobileMenuLink = styled.a`
  ${mobileMenuLinkBase}
  color: #3f4948;
  background: rgba(255, 255, 255, 0.72);

  &:hover {
    color: #006a64;
    background: rgba(0, 106, 100, 0.06);
    border-color: rgba(0, 106, 100, 0.12);
    transform: translateX(0.125rem);
  }

  &::after {
    content: '→';
    font-size: 0.875rem;
    color: rgba(0, 106, 100, 0.45);
    transition: transform 0.2s ease, color 0.2s ease;
  }

  &:hover::after {
    transform: translateX(0.125rem);
    color: #006a64;
  }
`;

const MobileMenuActiveLink = styled(MobileMenuLink)`
  color: #00514b;
  background: linear-gradient(135deg, rgba(82, 228, 212, 0.18) 0%, rgba(0, 106, 100, 0.1) 100%);
  border-color: rgba(0, 106, 100, 0.2);
  box-shadow: 0 0.25rem 0.875rem rgba(0, 106, 100, 0.1);

  &::after {
    color: #006a64;
  }
`;

const MobileMenuBottom = styled.div`
  margin-top: auto;
  padding-top: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  border-top: 1px solid rgba(0, 106, 100, 0.1);
`;

const MobileMenuLanguageField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const MobileMenuFieldLabel = styled.span`
  font-size: 0.6875rem;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(0, 106, 100, 0.55);
  padding: 0 0.125rem;
`;

const MobileMenuLanguageSelect = styled(TopNavLanguageSelect)`
  width: 100%;
  border-radius: 0.75rem;
  padding: 0.75rem 2.25rem 0.75rem 0.875rem;
`;

const MobileMenuLogin = styled(TopNavLogin)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(0, 106, 100, 0.16);
  background: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  transition:
    color 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease,
    transform 0.2s ease;

  &:hover {
    color: #006a64;
    border-color: rgba(0, 106, 100, 0.3);
    background: #ffffff;
    transform: none;
  }
`;

const MobileMenuBookDemo = styled(TopNavBookDemo)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.8125rem 1rem;
  border-radius: 0.75rem;
  font-weight: 600;
  box-shadow: 0 0.5rem 1.125rem rgba(0, 106, 100, 0.22);

  &:hover {
    transform: translateY(-0.0625rem);
  }
`;

const mobileMenuItemVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: (index) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: 0.05 + index * 0.04,
      duration: 0.22,
      ease: 'easeOut',
    },
  }),
};

function isNavLinkActive(pathname, href) {
  if (href === '/') return pathname === '/';
  if (href.startsWith('/') && href !== '/') return pathname.startsWith(href);
  return false;
}

const AUTH_NAV_PATHS = ['/signin', '/signup', '/forgot-password', '/reset-password'];

function isAuthNavPath(pathname) {
  return AUTH_NAV_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

export default function LandingNavbar() {
  const { pathname } = useLocation();
  const { t, language, changeLanguage } = useTranslation();

  const languageOptions = [
    { value: 'en', label: t('landingNav.langEn') },
    { value: 'tr', label: t('landingNav.langTr') },
  ];
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isTopNavScrolled, setIsTopNavScrolled] = useState(false);
  const prevBodyOverflowRef = useRef('');
  const showElevatedNav = isTopNavScrolled || isAuthNavPath(pathname);

  const onChangeLanguage = (event) => {
    changeLanguage(event.target.value);
  };

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 1024) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setIsTopNavScrolled(window.scrollY > 16);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    prevBodyOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevBodyOverflowRef.current;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileMenuOpen]);

  return (
    <TopNavShell $scrolled={showElevatedNav}>
      <TopNavBar aria-label={t('landingNav.ariaTopNav')}>
        <TopNavInner $scrolled={showElevatedNav}>
        <TopNavBrand to="/">
          <LandingLogoImage src="/TheRest360.png" alt="" />
          <LandingBrandWordmark>TheResto360</LandingBrandWordmark>
        </TopNavBrand>

        <TopNavLinks>
          {LANDING_NAV_LINKS.map((link) =>
            isNavLinkActive(pathname, link.href) ? (
              <TopNavActiveLink key={link.id} href={link.href}>
                {t(`landingNav.links.${link.id}`)}
              </TopNavActiveLink>
            ) : (
              <TopNavLink key={link.id} href={link.href}>
                {t(`landingNav.links.${link.id}`)}
              </TopNavLink>
            ),
          )}
        </TopNavLinks>

        <TopNavActions>
          <TopNavLogin to="/signin">{t('landingNav.logIn')}</TopNavLogin>
          <TopNavBookDemo to="/signup">{t('landingPage.bookDemo')}</TopNavBookDemo>
          <TopNavLanguageSelect
            value={language}
            onChange={onChangeLanguage}
            aria-label={t('landingNav.selectLanguage')}
            title={t('landingNav.selectLanguage')}
          >
            {languageOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TopNavLanguageSelect>
        </TopNavActions>

        <MobileNavActions>
          <MobileLanguageSelect
            value={language}
            onChange={onChangeLanguage}
            aria-label={t('landingNav.selectLanguage')}
            title={t('landingNav.selectLanguage')}
          >
            {languageOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </MobileLanguageSelect>
          <TopNavBookDemo to="/signup">{t('landingPage.bookDemo')}</TopNavBookDemo>
          <MobileMenuButton
            type="button"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-expanded={mobileMenuOpen}
            aria-controls="landing-mobile-menu"
            aria-label={mobileMenuOpen ? t('landingNav.closeMenu') : t('landingNav.openMenu')}
          >
            <MobileMenuIcon $open={mobileMenuOpen}>
              <span />
            </MobileMenuIcon>
          </MobileMenuButton>
        </MobileNavActions>
      </TopNavInner>

      <AnimatePresence initial={false}>
        {mobileMenuOpen ? (
          <>
            <MobileMenuBackdrop
              type="button"
              aria-label={t('landingNav.closeMenu')}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              onClick={() => setMobileMenuOpen(false)}
            />
            <MobileMenuPanel
              id="landing-mobile-menu"
              aria-label={t('landingNav.ariaTopNav')}
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <MobileMenuHeader>
                <MobileMenuBrand to="/" onClick={() => setMobileMenuOpen(false)}>
                  <MobileMenuBrandLogo src="/TheRest360.png" alt="" />
                  <MobileMenuBrandName>TheResto360</MobileMenuBrandName>
                </MobileMenuBrand>
                <MobileMenuCloseButton
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label={t('landingNav.closeMenu')}
                  title={t('landingNav.closeMenu')}
                >
                  <MobileMenuIcon $open>
                    <span />
                  </MobileMenuIcon>
                </MobileMenuCloseButton>
              </MobileMenuHeader>

              <MobileMenuBody>
                <MobileMenuSectionLabel>{t('landingNav.menu')}</MobileMenuSectionLabel>
                <MobileMenuLinks aria-label={t('landingNav.ariaTopNav')}>
                  {LANDING_NAV_LINKS.map((link, index) => {
                    const LinkComponent = isNavLinkActive(pathname, link.href)
                      ? MobileMenuActiveLink
                      : MobileMenuLink;

                    return (
                      <motion.div
                        key={link.id}
                        custom={index}
                        variants={mobileMenuItemVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <LinkComponent
                          href={link.href}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {t(`landingNav.links.${link.id}`)}
                        </LinkComponent>
                      </motion.div>
                    );
                  })}
                </MobileMenuLinks>

                <MobileMenuBottom>
                  <MobileMenuLanguageField>
                    <MobileMenuFieldLabel>{t('landingNav.selectLanguage')}</MobileMenuFieldLabel>
                    <MobileMenuLanguageSelect
                      value={language}
                      onChange={onChangeLanguage}
                      aria-label={t('landingNav.selectLanguage')}
                      title={t('landingNav.selectLanguage')}
                    >
                      {languageOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </MobileMenuLanguageSelect>
                  </MobileMenuLanguageField>
                  <MobileMenuLogin to="/signin" onClick={() => setMobileMenuOpen(false)}>
                    {t('landingNav.logIn')}
                  </MobileMenuLogin>
                  <MobileMenuBookDemo to="/signup" onClick={() => setMobileMenuOpen(false)}>
                    {t('landingPage.bookDemo')}
                  </MobileMenuBookDemo>
                </MobileMenuBottom>
              </MobileMenuBody>
            </MobileMenuPanel>
          </>
        ) : null}
      </AnimatePresence>
      </TopNavBar>
    </TopNavShell>
  );
}
